import { CommandKind } from '../../engine/commands/CommandBuffer';
import { SlimeState } from '../../engine/content/types';
import type { RuleRegistration, RuleRegistry } from '../../engine/content/types';
import type { GameContext } from '../../engine/core/GameContext';
import { runEmptyFieldPace } from '../spawning/pacing';
import { runSpawnWave } from '../spawning/spawnWave';

/**
 * 越线即结算：本步新越过红线的史莱姆计一次漏网，并在原地爆开消失（KillSlime，value −1 标记这不是爆浆），
 * 红线因此是可见的边界，而不是一条落地前才起作用的线。
 */
function countLeaks(context: GameContext): void {
  const { slimes, level, state, commands } = context;
  for (let id = 0; id < slimes.capacity; id += 1) {
    if (slimes.active[id] === 0 || slimes.isDying(id) || (slimes.flags[id] & SlimeState.Leaked) !== 0) continue;
    if (slimes.y[id] <= level.world.leakLine) continue;
    slimes.flags[id] |= SlimeState.Leaked;
    state.leaked += 1;
    // a 位是 KillSlime 的 score，进 EntityDied 的 value：−1 让反馈与统计知道这不是爆浆。
    commands.resolution.push(CommandKind.KillSlime, -1, id, 0, 0, 0, -1);
  }
}

/** 红线以上的活动史莱姆数。通关只看它：越线的当步已结算消失，本步内仍可能未回收。 */
function airborne(context: GameContext): number {
  const { slimes, level } = context;
  let count = 0;
  for (let id = 0; id < slimes.capacity; id += 1) {
    if (slimes.active[id] !== 0 && !slimes.isDying(id) && slimes.y[id] <= level.world.leakLine) count += 1;
  }
  return count;
}

export function createRuleRegistry(): RuleRegistry {
  return new Map<string, RuleRegistration>([
    // 投放：关卡是投放时间表而不是坐标表。实现见 mechanics/spawning。
    ['core:spawn-wave', { phase: 'beforePhysics' as const, execute: runSpawnWave }],
    // 节奏：空场提速。归关卡规则，与波次表配套；坐标表关卡与 Fixture 不带它。
    ['core:empty-field-pace', { phase: 'beforePhysics' as const, execute: runEmptyFieldPace }],
    // 失败：漏网预算。越过红线即计一次漏网并在红线处爆开消失，累计达到上限判负，与通关看的是同一条红线。
    // 不量堆顶高度也不量自由宽度——前者第一层就填满底边，后者在堆快满时靠持续射击引发连锁就能自清。
    ['core:leak-budget', {
      phase: 'afterPhysics' as const,
      execute: (context, params) => {
        const max = typeof params.max === 'number' ? params.max : Number.POSITIVE_INFINITY;
        countLeaks(context);
        if (context.state.leaked >= max) context.commands.endOfStep.push(CommandKind.EndLevel, -1, -1, 0, 0, 0, 0);
      },
    }],
    // 通关：投放跑完且红线以上清空。投放未完成时空中为零只是波间空档，不算赢。
    ['core:clear-above-line', {
      phase: 'afterFlush' as const,
      execute: (context) => {
        if (context.spawn.finished && airborne(context) === 0) context.commands.endOfStep.push(CommandKind.EndLevel, -1, -1, 0, 0, 0, 1);
      },
    }],
    // 全场清空。留给坐标表关卡与测试 Fixture；正式关卡用 core:clear-above-line。
    ['core:clear-all', {
      phase: 'afterFlush' as const,
      execute: (context) => {
        if (context.slimes.activeCount === 0) context.commands.endOfStep.push(CommandKind.EndLevel, -1, -1, 0, 0, 0, 1);
      },
    }],
  ]);
}
